'use client';

import Head from 'next/head';
import { useEffect } from 'react';
import Script from 'next/script';
import '../styles/globals.css';
import { motion, AnimatePresence } from 'framer-motion';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { AdminAuthProvider } from '../contexts/AdminAuthContext';
import { QuoteProvider } from '../contexts/QuoteContext';
import Header from '../components/Header';
import Modal from '../components/Modal';
import ContactModal from '../components/ContactModal';
import CookieConsentBanner from '../components/CookieConsent';
import { initGA, pageview, hasAnalyticsConsent } from '../utils/analytics';
import { setupSecurityListeners } from '../utils/security';
import { validateEnvironment, logEnvironmentStatus } from '../utils/envValidation';

function MyApp({ Component, pageProps }) {
  const router = useRouter();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState(null);

  const openModal = (content, size = 'lg') => {
    setModalContent({ content, size });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalContent(null);
  };

  // Log route changes
  useEffect(() => {
    console.log('[DEBUG] MyApp: Route changed to', router.route);
  }, [router.route]);

  // Initialize Google Analytics and Security
  useEffect(() => {
    // Validate environment variables
    try {
      validateEnvironment();
      logEnvironmentStatus();
    } catch (error) {
      console.error('Environment validation failed:', error.message);
    }

    initGA();
    setupSecurityListeners();
  }, []);

  // Track page views
  useEffect(() => {
    const handleRouteChange = (url) => {
      if (hasAnalyticsConsent()) {
        pageview(url);
      }
    };

    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events]);

  // Suppress Next.js router abort errors in development
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      const originalConsoleError = console.error;
      console.error = (...args) => {
        // Suppress "Abort fetching component" errors during hot reloading
        if ((args[0] && typeof args[0] === 'string' && args[0].includes('Abort fetching component')) ||
            (args[0] instanceof Error && args[0].message && args[0].message.includes('Abort fetching component'))) {
          console.log('Suppressed Abort fetching component error:', args[0]);
          return;
        }
        originalConsoleError.apply(console, args);
      };
    }
  }, []);

  // Check if current route is admin route
  const isAdminRoute = router.pathname.startsWith('/admin');

  return (
    <>
      <Head>
        <link rel="icon" href="/images/favico.png" />
      </Head>

      {/* Google Analytics Script */}
      {process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID && process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID !== 'G-XXXXXXXXXX' && (
        <Script
          strategy="afterInteractive"
          src={`https://www.googletagmanager.com/gtag/js?id=${process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID}`}
        />
      )}
      <QuoteProvider>
        <AdminAuthProvider>
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={router.route}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <Component {...pageProps} />
            </motion.div>
          </AnimatePresence>

          {/* Modal System */}
          {modalContent?.content === 'contact' ? (
            <ContactModal
              isOpen={isModalOpen}
              onClose={closeModal}
            />
          ) : (
            <Modal
              isOpen={isModalOpen}
              onClose={closeModal}
              size={modalContent?.size || 'lg'}
            >
              {modalContent?.content}
            </Modal>
          )}

          {/* Cookie Consent Banner */}
          {!isAdminRoute && <CookieConsentBanner />}
        </AdminAuthProvider>
      </QuoteProvider>
    </>
  );
}

export default MyApp;